import React, { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    LiteTradePortfolioListItemWrapperStyled,
    LiteTradePortfolioListStyled,
    LiteTradePortfolioStyled,
    LiteTradePortfolioSumPlStyled,
    LiteTradePortfolioTitleStyled,
} from './styled/LiteTradePortfolioStyled';
import { getLiteTradeAccountPortfolio, getLiteTradeAssets, getLiteTradeMarketData } from '../../liteTradeSelectors';
import _ from 'lodash';
import Asset from '../../../Asset/Asset';
import { Alert } from 'react-bootstrap';
import SlideMobilePositionModal
    from '../../../../mobileAppCore/components/MobileSliderPages/slides/SlideMobilePositionModal';
import { getUserSelectedAccountCode, getUserSelectedAccountType } from '../../../../app/appSelectors';
import LiteTradePortfolioPositionRow from './LiteTradePortfolioPositionRow';
import LiteTradePortfolioOrderRow from './LiteTradePortfolioOrderRow';
import SlideMobileOrderModal from '../../../../mobileAppCore/components/MobileSliderPages/slides/SlideMobileOrderModal';
import { addMarketDataWatchAction, removeMarketDataWatchAction } from '../../liteTradeActions';
import { useLangContext } from '../../../../providers/LangProvider/LangProviderContext';
import {
    LITE_TRADE_ORDER_STATUSES,
    LITE_TRADE_POSITION_EFFECTS,
    LITE_TRADE_POSITION_SIDE,
} from '../../liteTradeConstants';
import amountFormat from '../../../../utils/amountFormat';
import {
    ILiteTradeAssetSymbol,
    ILiteTradePortfolioOrder,
    ILiteTradePortfolioPosition,
} from '../../types/liteTradeReducer.t';
import { useSelectorWithDebounced } from '../../../../hooks/useSelectorWithDebounced';
import { logAppsflyerAction } from '../../../../app/appActions';
import { LOG_AF_EVENT_NAMES } from '../../../../app/appConstants';
import BinIcon from '../../../../components/icon/IconWrapped';
import SlideMobileCloseAllPositions
    from '../../../../mobileAppCore/components/MobileSliderPages/slides/SlideMobileCloseAllPositions/SlideMobileCloseAllPositions';
import classNames from 'classnames';
import useAccountStorage from '../../../../hooks/storage/useAccountStorage';
import MobileRecommended from '../../../../mobileAppCore/components/MobileRecommended/MobileRecommended';

interface ILiteTradePortfolioProps {
    className?: string,
    fromComponentName: 'portfolio' | 'portfolioSymbol',
    onSelectPosition?: (selectedPosition: ILiteTradePortfolioPosition) => void,
    onSelectOrder?: (selectedOrder: ILiteTradePortfolioOrder) => void,
    symbol?: ILiteTradeAssetSymbol,
}

const LiteTradePortfolio = (props: ILiteTradePortfolioProps) => {
    const {
        className,
        fromComponentName,
        onSelectPosition,
        onSelectOrder,
        symbol,
    } = props;
    const dispatch = useDispatch();
    const addMarketDataWatch = useCallback((payload) => dispatch(addMarketDataWatchAction(payload)), [dispatch]);
    const removeMarketDataWatch = useCallback((payload) => dispatch(removeMarketDataWatchAction(payload)), [dispatch]);
    const assets = useSelector(getLiteTradeAssets);
    const portfolio = useSelector(getLiteTradeAccountPortfolio);
    const selectedAccountCode = useSelector(getUserSelectedAccountCode);
    const marketData = useSelectorWithDebounced(getLiteTradeMarketData);
    const selectedAccountType = useSelector(getUserSelectedAccountType);

    const { getLangPath } = useLangContext();
    const [selectedPosition, setSelectedPosition] = useState(null);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [positions, setPositions] = useState<ILiteTradePortfolioPosition[]>([]);
    const [orders, setOrders] = useState<ILiteTradePortfolioOrder[]>([]);
    const [showModalCloseAllPositions, setShowModalCloseAllPositions] = useState(false);
    const [isPortfolioInited, setIsPortfolioInited] = useState(false);
    const { getAccountStorageItem, setAccountStorageItem } = useAccountStorage(selectedAccountCode);

    const hasPositions = useMemo(() => {
        return positions.length > 0;
    }, [positions]);

    const hasOrders = useMemo(() => {
        return orders.length > 0;
    }, [orders]);

    const extendPortfolioItem = useCallback((item, type) => {
        const asset = _.find(assets, { symbol: item[type === 'position' ? 'symbol' : 'instrument'] });
        if (!asset) return {};
        const assetInstance = new Asset(asset, {
            assets,
            selectedAccountCode,
        });
        return {
            ...item,
            assetInstance,
        };
    }, [assets, selectedAccountCode]);

    const addLinksToPosition = useCallback((item) => {
        return {
            ...item,
            links: portfolio.orders.filter(order =>
                order.legs[0].positionCode === item.positionCode &&
                order.status === LITE_TRADE_ORDER_STATUSES.WORKING,
            ),
        };
    }, [portfolio.orders]);

    const addLinksToOrder = useCallback((item) => {
        if (!item.links) {
            return item;
        }
        return {
            ...item,
            links: item.links.map(link => (portfolio.orders.find(order => order.orderCode === link.linkedOrder))),
        };
    }, [portfolio.orders]);

    useEffect(() => {
        positions.forEach(item => {
            if (!symbol || symbol === item.symbol) {
                addMarketDataWatch({ fromComponentName, symbol: item.symbol });
            }
        });
        orders.forEach(item => {
            if (!symbol || symbol === item.instrument) {
                addMarketDataWatch({ fromComponentName, symbol: item.instrument });
            }
        });
        return () => {
            removeMarketDataWatch({ fromComponentName });
        };
    }, [addMarketDataWatch, fromComponentName, orders, positions, removeMarketDataWatch, symbol]);

    useEffect(() => {
        if (assets.length) {
            if (portfolio?.positions) {
                setPositions(portfolio.positions
                    .filter(item => !symbol || item.symbol === symbol)
                    .filter(item => item.quantity > 0)
                    .map(item => extendPortfolioItem(item, 'position'))
                    .map(addLinksToPosition));

                setOrders(portfolio.orders
                    .filter(item => !symbol || item.instrument === symbol)
                    .filter(item => item.legs[0].positionEffect === LITE_TRADE_POSITION_EFFECTS.OPEN)
                    .filter(item => item.status === 'WORKING')
                    .map(item => extendPortfolioItem(item, 'order'))
                    .map(addLinksToOrder));
                setIsPortfolioInited(true);
            }
        }
    }, [assets, portfolio, symbol, extendPortfolioItem, addLinksToOrder, addLinksToPosition, getAccountStorageItem, setAccountStorageItem]);

    const handleSelectPosition = useCallback((e) => {
        setSelectedPosition(e.currentTarget.dataset.id);
        const selectedPosition = _.find(positions, { positionCode: e.currentTarget.dataset.id });
        onSelectPosition && onSelectPosition(selectedPosition);
    }, [positions, onSelectPosition]);

    const handleSelectOrder = useCallback((e) => {
        setSelectedOrder(e.currentTarget.dataset.id);
        const selectedOrder = _.find<ILiteTradePortfolioOrder>(orders, { orderId: parseInt(e.currentTarget.dataset.id) });
        onSelectOrder && onSelectOrder(selectedOrder);
    }, [onSelectOrder, orders]);

    const selectedPositionInfo = useMemo(() =>
            _.find(positions, { positionCode: selectedPosition }),
        [positions, selectedPosition]);

    const selectedOrderInfo = useMemo(() =>
            _.find(orders, { orderId: parseInt(selectedOrder) }),
        [orders, selectedOrder]);

    const sumPl = useMemo(() => {
        let ready = true;
        const amount = positions.reduce((acc, cur) => {
            const side = cur.side === LITE_TRADE_POSITION_SIDE.BUY ? 'bid' : 'ask';
            if (!marketData?.[cur.symbol]) ready = false;
            const pl = cur.assetInstance.getPositionPL(cur, marketData?.[cur.symbol]?.[side] ?? 0);
            return acc + +pl;
        }, 0);

        return !!ready ? amount : 0;
    }, [marketData, positions]);

    const handleCloseMobilePositionModal = useCallback((action) => {
        dispatch(logAppsflyerAction({
            name: LOG_AF_EVENT_NAMES.ASSET_POSITION_CLOSE,
            payload: {
                account_type: selectedAccountType,
                asset: selectedPositionInfo.symbol,
                action,
                position: {
                    position_code: selectedPositionInfo.positionCode,
                    quantity: selectedPositionInfo.quantity,
                    side: selectedPositionInfo.side,
                },
            },
        }));
        setSelectedPosition(null);
    }, [dispatch, selectedAccountType, selectedPositionInfo]);

    const handleCloseMobileOrderModal = useCallback((action) => {
        dispatch(logAppsflyerAction({
            name: LOG_AF_EVENT_NAMES.ASSET_ORDER_CLOSE,
            payload: {
                account_type: selectedAccountType,
                asset: selectedOrderInfo.instrument,
                action,
                order: {
                    quantity: selectedOrderInfo.legs[0].quantity,
                    side: selectedOrderInfo.side,
                    order_code: selectedOrderInfo.orderCode,
                    type: selectedOrderInfo.type,
                },
            },
        }));
        setSelectedOrder(null);
    }, [dispatch, selectedAccountType, selectedOrderInfo]);

    const sortedPositions = useMemo(() => {
        const active = [];
        const inactive = [];
        positions.forEach(item => {
            if (item.assetInstance.isTradingNow()) {
                active.push(item);
            } else {
                inactive.push(item);
            }
        });
        return [...active, ...inactive];
    }, [positions]);

    const sortedOrders = useMemo(() => {
        const active = [];
        const inactive = [];
        orders.forEach(item => {
            if (item.assetInstance.isTradingNow()) {
                active.push(item);
            } else {
                inactive.push(item);
            }
        });
        return [...active, ...inactive];
    }, [orders]);

    const hasInactivePositions = useMemo(() => {
        return positions.some((position) => !position.assetInstance.isTradingNow());
    }, [positions]);

    const closeAllPositionsHandler = useCallback(() => {
        if (hasInactivePositions) return;
        setShowModalCloseAllPositions(true);
    }, [hasInactivePositions]);

    if (!isPortfolioInited) {
        return (
            <LiteTradePortfolioStyled className={classNames('lite-trade-portfolio', className)}>
                <LiteTradePortfolioTitleStyled>
                    <h6 className={'mb-0'}>{getLangPath('lite_version.home.positions')}</h6>
                    <LiteTradePortfolioSumPlStyled className="skeleton-br16" style={{
                        width: 76,
                        height: 32,
                    }}>sumPl</LiteTradePortfolioSumPlStyled>
                </LiteTradePortfolioTitleStyled>
                <div className="skeleton-br16" style={{ height: 88 }}/>
            </LiteTradePortfolioStyled>
        );
    }

    return <LiteTradePortfolioStyled className={classNames('lite-trade-portfolio', className)}>
        {
            !!selectedPositionInfo && <SlideMobilePositionModal
                accountCode={selectedAccountCode}
                assetInstance={selectedPositionInfo.assetInstance}
                onClose={handleCloseMobilePositionModal}
                positionCode={selectedPosition}
                symbol={selectedPositionInfo.symbol}
                openAssetOnClick={!symbol}
            />
        }
        {
            !!selectedOrderInfo && <SlideMobileOrderModal
                assetInstance={selectedOrderInfo.assetInstance}
                orderId={selectedOrder}
                onClose={handleCloseMobileOrderModal}
                symbol={selectedOrderInfo.instrument}
                openAssetOnClick={!symbol}
            />
        }
        {
            <SlideMobileCloseAllPositions
                show={showModalCloseAllPositions}
                positions={positions}
                sumPl={sumPl}
                symbol={symbol}
                onClose={() => setShowModalCloseAllPositions(false)}
            />
        }
        {
            !hasOrders && !hasPositions && <>
                {
                    !!symbol
                        ? <Alert variant={'info-outside'}>{getLangPath('lite_version.order.no_positions_yet')}</Alert>
                        : <MobileRecommended/>
                }
            </>
        }
        {
            hasPositions && <>
                <LiteTradePortfolioTitleStyled>
                    <h6 className={'mb-0'}>{getLangPath('lite_version.home.positions')}</h6>
                    <LiteTradePortfolioSumPlStyled onClick={closeAllPositionsHandler}>
                        {
                            !hasInactivePositions && <BinIcon name={'IcClose16'} size={16}/>
                        }
                        <span
                            style={{ color: sumPl > 0 ? 'var(--color-label-positive)' : 'var(--color-label-negative)' }}>{amountFormat(sumPl, 2, '$', false, {
                            toFixed: true,
                        })}</span>
                    </LiteTradePortfolioSumPlStyled>
                </LiteTradePortfolioTitleStyled>
                <LiteTradePortfolioListStyled>
                    {
                        sortedPositions.map(item => (
                            <Fragment key={item.positionCode}>
                                <LiteTradePortfolioListItemWrapperStyled
                                    data-id={item.positionCode}
                                    onClick={handleSelectPosition}>
                                    <LiteTradePortfolioPositionRow
                                        item={item}
                                        symbol={item.symbol}
                                        from={symbol ? 'assetInfo' : 'portfolio'}
                                    />
                                </LiteTradePortfolioListItemWrapperStyled>
                            </Fragment>),
                        )
                    }
                </LiteTradePortfolioListStyled>
            </>
        }
        {
            hasOrders && <>
                <LiteTradePortfolioTitleStyled>
                    <h6 className={'mb-0'}>{getLangPath('lite_version.home.orders')}</h6>
                </LiteTradePortfolioTitleStyled>
                <LiteTradePortfolioListStyled>
                    {
                        sortedOrders.map(item => (
                            <Fragment key={item.orderCode}>
                                <LiteTradePortfolioListItemWrapperStyled
                                    data-id={item.orderId}
                                    onClick={handleSelectOrder}>
                                    <LiteTradePortfolioOrderRow
                                        item={item}
                                        from={symbol ? 'assetInfo' : 'portfolio'}/>
                                </LiteTradePortfolioListItemWrapperStyled>
                            </Fragment>),
                        )
                    }
                </LiteTradePortfolioListStyled>
            </>
        }
    </LiteTradePortfolioStyled>;
};

export default LiteTradePortfolio;
